import { useState, useCallback } from "react";
import axios from "axios";
import {
  IncidentQueryField,
  IncidentRecord,
  FilterCriteria,
  OrderByCriteria,
} from "../types/salesforce";

const { VITE_SALESFORCE_API_URL } = import.meta.env;

const API_URL = VITE_SALESFORCE_API_URL;

export function useSalesforce() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableFields, setAvailableFields] = useState<IncidentQueryField[]>(
    []
  );

  const getQueryFields = useCallback(async (): Promise<
    IncidentQueryField[]
  > => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/metadata`);

      const parsedData = JSON.parse(response.data);

      if (!parsedData.success) {
        throw new Error(parsedData.error || "Failed to fetch fields");
      }

      const fields = parsedData.fields || [];
      setAvailableFields(fields);
      return fields;
    } catch (err) {
      console.error("API Error:", err);
      const message = err instanceof Error ? err.message : "An error occurred";
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getPicklistValues = useCallback(
    async (fieldName: string): Promise<string[]> => {
      setIsLoading(true);
      setError(null);
      try {
        const field = availableFields.find(
          (f) => f.Field_API_Name__c === fieldName
        );

        // If it's a custom picklist using Values__c
        if (field?.Treat_as_Picklist__c && field.Values__c) {
          return field.Values__c.split("\n")
            .map((value) => value.trim())
            .filter((value) => value.length > 0);
        }

        // Otherwise, fetch from Salesforce
        const response = await axios.get(`${API_URL}/picklist/${fieldName}`);
        const parsedData = JSON.parse(response.data);

        console.log({ parsedData });

        if (!parsedData.success) {
          throw new Error(
            parsedData.error || "Failed to fetch picklist values"
          );
        }

        // If there are restricted values defined, filter the picklist values
        if (field?.Values__c) {
          const allowedValues = field.Values__c.split("\n")
            .map((value) => value.trim())
            .filter((value) => value.length > 0);
          return (parsedData.values || []).filter((value: any) =>
            allowedValues.includes(value)
          );
        }

        return parsedData.values || [];
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "An error occurred";
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [availableFields]
  );

  const buildFilterString = (
    filter: FilterCriteria,
    fields: IncidentQueryField[]
  ): string => {
    const field = fields.find((f) => f.Field_API_Name__c === filter.fieldName);
    console.log("Building filter for:", {
      filter,
      field,
      fieldType: field?.Field_Type__c,
    });

    let value = filter.value;

    // Handle Incident_Score__c field
    if (filter.fieldName === "Incident_Score__c") {
      // Convert value to number
      const numericValue = Number(value);
      if (!isNaN(numericValue)) {
        return `${filter.fieldName} ${filter.operator} ${numericValue}`;
      }
    }

    // Handle specific field types first
    if (field) {
      switch (field.Field_Type__c) {
        case "Checkbox":
          // Handle empty or false values
          if (!value || value === "false") {
            return `(${filter.fieldName} = false OR ${filter.fieldName} = null)`;
          }
          return `${filter.fieldName} = true`;
        case "Date":
        case "DateTime":
          return `${filter.fieldName} ${filter.operator} ${value}`;
        case "Number":
        case "Currency":
        case "Percent":
          return `${filter.fieldName} ${filter.operator} ${value}`;
      }
    }

    // Handle special operators
    if (["LIKE", "NOT LIKE", "CONTAINS"].includes(filter.operator)) {
      value =
        filter.operator === "CONTAINS"
          ? `'%${filter.value}%'`
          : `'${filter.value}'`;
      return `${filter.fieldName} ${filter.operator} ${value}`;
    }

    if (["IN", "NOT IN"].includes(filter.operator)) {
      const values = Array.isArray(filter.value)
        ? filter.value
        : [filter.value];
      const quotedValues = values.map((v) => `'${v}'`);
      const valuesString = quotedValues.join(", ");
      return `${filter.fieldName} ${filter.operator} (${valuesString})`;
    }

    // Default case - wrap in quotes
    return `${filter.fieldName} ${filter.operator} '${value}'`;
  };

  const buildOrderByClause = (orderBy: OrderByCriteria | null): string => {
    if (!orderBy?.fieldName) return "";

    const field = availableFields.find(
      (f) => f.Field_API_Name__c === orderBy.fieldName
    );

    if (field?.Treat_as_Picklist__c && field.Values__c) {
      const values = field.Values__c.split("\n")
        .map((value) => value.trim())
        .filter((value) => value.length > 0);

      if (values.length > 0) {
        const caseStatement = values
          .map(
            (value, index) =>
              `WHEN ${field.Field_API_Name__c} = '${value}' THEN ${index}`
          )
          .join(" ");

        return ` ORDER BY (CASE ${caseStatement} ELSE ${values.length} END) ${orderBy.direction}`;
      }
    }

    return ` ORDER BY ${orderBy.fieldName} ${orderBy.direction}`;
  };

  const queryIncidents = useCallback(
    async (
      facilityId: string[],
      selectedFields: string[],
      filters: FilterCriteria[],
      logicExpression: string | null,
      orderBy: OrderByCriteria | null,
      setQuery: any
    ): Promise<IncidentRecord[]> => {
      setIsLoading(true);
      setError(null);
      try {
        const fields = await getQueryFields(); // Get fresh field data
        const fieldsToQuery =
          selectedFields.length > 0 ? selectedFields : ["Id"];
        const selectedFieldsString = fieldsToQuery.join(", ");
        const quotedFacilityIds = facilityId.map((id: string) => `'${id}'`);
        const facilityCondition = quotedFacilityIds.join(", ");
        let query = `SELECT ${selectedFieldsString} FROM Incident__c WHERE Facility__c IN (${facilityCondition})`;

        if (filters.length > 0) {
          if (!logicExpression) {
            const filterStrings = filters.map((filter) =>
              buildFilterString(filter, fields)
            );
            query += ` AND ${filterStrings.join(" AND ")}`;
          } else {
            let whereClause = logicExpression;
            filters.forEach((filter, index) => {
              const filterString = buildFilterString(filter, fields);
              whereClause = whereClause.replace(
                new RegExp(`\\b${index + 1}\\b`),
                `(${filterString})`
              );
            });

            whereClause = whereClause
              .replace(/\bAND\b/gi, "AND")
              .replace(/\bOR\b/gi, "OR")
              .trim();

            query += ` AND (${whereClause})`;
          }
        }

        // Add custom order by clause
        const orderByClause = buildOrderByClause(orderBy);
        if (orderByClause) {
          query += orderByClause;
        }

        console.log("Executing query:", query);

        if (setQuery) setQuery(query);

        const response = await axios.post(`${API_URL}/query`, { query });
        const parsedData =
          typeof response.data === "string"
            ? JSON.parse(response.data)
            : response.data;

        if (!parsedData.success) {
          throw new Error(parsedData.error || "Query failed");
        }

        return (parsedData.records || []).map((record: any) => {
          const transformedRecord: IncidentRecord = {} as any;
          for (const field of fieldsToQuery) {
            transformedRecord[field] = record[field];
          }
          return transformedRecord;
        });
      } catch (err) {
        console.error("Error in queryIncidents:", err);
        const message =
          err instanceof Error ? err.message : "An error occurred";
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    isLoading,
    error,
    getQueryFields,
    getPicklistValues,
    queryIncidents,
    availableFields,
  };
}
